import {inject, Injectable, NgZone} from '@angular/core';
import {BehaviorSubject, catchError, Observable, of, switchMap, timeout} from "rxjs";
import {ExternalApiService} from "../../../../core/api/external-api/external-api.service";
import * as Sentry from "@sentry/angular";
import {ConfigService} from "../../../../core/config";
import {CbdResultDto} from "../../../../core/dto/cbd-result-dto";
import {CreateCbdRequestDto} from "../../../../core/dto/create-cbd-dto";
import {Picture, PictureStatus} from "../../../../core/models/picture";
import {DomSanitizer} from "@angular/platform-browser";
import {TyrePicture} from "../../../../core/models/tyrePicture";
import {isEqual} from "lodash";
import {Step, StepType} from "../../../../core/models/step";
import {Analysis} from "../../../../core/models/analysis";
import {IndexedDBService} from "../../../../shared/services/indexed-db.service";

@Injectable({
    providedIn: 'root'
})
export class PhotoshootService {

  private readonly _sessionStorage = sessionStorage;

  protected externalApi = inject(ExternalApiService);
  protected config = inject(ConfigService);
  protected ngZone = inject(NgZone);
  private sanitizer = inject(DomSanitizer);
  private indexedDB = inject(IndexedDBService);

  private readonly _pictures: BehaviorSubject<TyrePicture[]> = new BehaviorSubject<TyrePicture[]>([]);
  readonly pictures$ = this._pictures.asObservable();

  private static readonly CBD_TIMEOUT = 5000;

  sendCBD(analysisId: number, step: Step, blob: Blob, dimensions: {width: number, height: number}) {
      const request = new CreateCbdRequestDto(Step.getTyrePositionDto(step.position), dimensions.width, dimensions.height);
      return this.externalApi.createCbd(analysisId, step.type, request, new File([blob], 'cbd.jpg')).pipe(
          switchMap((response) => this.setMercureEventSource(response.mercureTopic, response.sseTyreAnalysisToken)),
          catchError((error) => {
              Sentry.captureMessage(`Unable to send ${step.type.toLowerCase()} picture for CBD`, {extra: {error}});
              return of({isCBDValid: undefined} as CbdResultDto);
          }),
          timeout(PhotoshootService.CBD_TIMEOUT)
        )
  }

  private setMercureEventSource(topic: string, token: string): Observable<CbdResultDto> {
      return new Observable((observer) => {
          const url = new URL(this.config.mercureUrl);
          url.searchParams.append('topic', topic);
          url.searchParams.append(
              'authorization',
              token
          );
          const eSource = new EventSource(url.toString(), {
              withCredentials: false
          });
          eSource.onerror = (event) => {
              Sentry.captureException(new Error('CBD Mercure event error'), {extra: {event}});
              observer.error(new Error('CBD Mercure event error'));
          };
          eSource.onmessage = (event) => {
              this.ngZone.run(() => {
                  const result: CbdResultDto = JSON.parse(event.data)
                  observer.next(result);
                  observer.complete();
              })
              eSource.close();
          }

          return () => {
              eSource.close();
          }
      })
  }

  /**
   * Restores the photoshoot pictures from a server analysis
   * merges the server tyre statuses by position with the local pictures,
   * and purges the local pictures when the server returned a different analysis.
   * Must be awaited before navigating to the photoshoot so the right step is selected.
   */
  async restoreFromAnalysis(analysis: Analysis, previousAnalysisId?: number) {
    if (previousAnalysisId && previousAnalysisId !== analysis.id) {
      await this.indexedDB.deleteAllPictures();
      this.clearPictures();
    }

    const serverPictures = [
      ...analysis.referenceTyres.map(rt => TyrePicture.fromAnalysis(rt, StepType.REFERENCE)),
      ...analysis.wearTyres.map(wt => TyrePicture.fromAnalysis(wt, StepType.WEAR)),
    ].filter((p): p is TyrePicture => !!p);
    const localPictures = await this.indexedDB.getAllTyresPictures();

    const serverByKey = new Map(serverPictures.map(p => [p.step.toString(), p] as const));
    const localByKey = new Map(localPictures.map(p => [p.step.toString(), p] as const));
    const keys = new Set<string>([...serverByKey.keys(), ...localByKey.keys()]);

    const mergedPictures: TyrePicture[] = [];
    for (const key of keys) {
      const sp = serverByKey.get(key);
      const lp = localByKey.get(key);

      if (lp && sp) {
        // The server is authoritative when it says the picture is uploaded,
        // otherwise the local status wins (it is the only one knowing about a pending upload).
        // The local instance is updated in place: spreading it would return a plain object and
        // lose the TyrePicture prototype, which callers rely on (Picture.toFile() on re-upload).
        if (sp.status === PictureStatus.VALID) {
          lp.status = PictureStatus.VALID;
        }
        mergedPictures.push(lp);
      } else if (lp && !sp) {
        mergedPictures.push(lp);
      } else if (!lp && sp) {
        mergedPictures.push(sp);
      }
    }

    this.setPictures(mergedPictures);

    await this.indexedDB.deleteAllPictures();
    await this.indexedDB.bulkAdd(mergedPictures);
    return mergedPictures;
  }

  async associatePictureBlob(picture: Picture, blob: Blob) {
    const buffer = await blob.arrayBuffer();
    picture.data = new Uint8Array(buffer.slice(0));
    picture.url = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
    return picture;
  }

  getPictures(): TyrePicture[] {
    return this._pictures ? this._pictures.getValue() : [];
  }

  setPicture(picture: TyrePicture) {
    const pictures = this.getUpdatedPictures(picture, this.getPictures());
    this._pictures.next(pictures);
  }

  setPictures(p: TyrePicture[]) {
    this._pictures.next([...p]);
  }

  clearPictures() {
    this._pictures.next([]);
  }

  // Emit new array to notify signals
  private getUpdatedPictures(picture: TyrePicture, savedPictures: TyrePicture[]) {
    const existingIndex = savedPictures.findIndex(p => isEqual(p.step, picture.step));
    if (existingIndex === -1) {
      return [...savedPictures, picture];
    }
    const existingPicture = savedPictures[existingIndex];
    existingPicture.url = picture.url;
    existingPicture.data = picture.data;
    existingPicture.status = picture.status;
    return [...savedPictures];
  }

  // TODO: Do not use session storage directly here.
  setCamera(deviceId: string, step: StepType) {
    this._sessionStorage.setItem(`camera.${step}`, deviceId)
  }

  getCamera(step: StepType) {
    return this._sessionStorage.getItem(`camera.${step}`);
  }
}
